// Deterministic loader for the legacy jQuery / Qode theme scripts.
//
// These used to be injected as <Helmet><script src="..." /></Helmet>. That was
// unreliable: react-helmet 6 manages <head> declaratively and removes tags it
// believes it owns, and under React 18 StrictMode the mount → unmount → remount
// cycle races with it. Measured on production, four consecutive loads of
// /collection injected the scripts exactly once — the other three left the
// carousel as a static row of slides with no autoplay and dead arrows.
//
// This appends each script to <body> once, keyed by src, and never removes it.
// Callers get a promise so they can initialise against whatever globals the
// script defines, instead of hoping it arrived.
//
// Two things worth knowing:
//
//   * Paths must be absolute ("/assets/js/x.js"). The Helmet tags used relative
//     paths — "assets/js/x.js" from Home, "../../assets/js/x.js" from
//     Collection — which resolve against the current route. They happened to
//     work at "/" and "/collection" and would 404 on any deeper path.
//
//   * These scripts self-initialise on $(document).ready(). Because we append
//     after mount the document is already ready, so jQuery runs the handler
//     immediately and the React DOM it needs is present. Loading them from
//     index.html instead would run them before React had rendered anything.

const cache = new Map();

export const loadScript = (src) => {
  const pending = cache.get(src);
  if (pending) return pending;

  const promise = new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = src;
    // Preserve execution order for scripts appended in sequence.
    el.async = false;
    el.onload = () => resolve(src);
    el.onerror = () => {
      // Drop the rejected entry so a later mount can retry.
      cache.delete(src);
      reject(new Error(`Failed to load ${src}`));
    };
    document.body.appendChild(el);
  });

  cache.set(src, promise);
  return promise;
};

// Load in strict sequence — later scripts may depend on earlier globals.
export const loadScripts = (sources) =>
  sources.reduce((chain, src) => chain.then(() => loadScript(src)), Promise.resolve());
